<template>
  <HeadlessTransitionRoot
    appear
    :show="show"
    as="template"
  >
    <HeadlessDialog
      as="div"
      class="relative z-50"
      @close="emit('close')"
    >
      <HeadlessTransitionChild
        as="template"
        enter="duration-300 ease-out"
        enter-from="opacity-0"
        enter-to="opacity-100"
        leave="duration-200 ease-in"
        leave-from="opacity-100"
        leave-to="opacity-0"
      >
        <div class="fixed inset-0 bg-black bg-opacity-25">
          <MouseFollower :hide-when-over-el="dialogPanelEl" />
        </div>
      </HeadlessTransitionChild>

      <div class="fixed bottom-0 left-0 top-0 w-screen overflow-y-auto">
        <div class="flex min-h-full items-center justify-center p-4 text-center">
          <HeadlessTransitionChild
            as="template"
            enter="duration-300 ease-out"
            enter-from="opacity-0 scale-95"
            enter-to="opacity-100 scale-100"
            leave="duration-200 ease-in"
            leave-from="opacity-100 scale-100"
            leave-to="opacity-0 scale-95"
          >
            <HeadlessDialogPanel class="w-full max-w-4xl rounded-2xl shadow-xl transition-all">
              <div
                ref="dialogPanelEl"
                class="w-full transform rounded-2xl bg-white px-8 py-12 relative"
              >
                  <button
                      class="shrink-0 absolute top-5 right-5"
                      type="button"
                      @click="emit('close')"
                  >
                      <IconClose class="h-8" />
                  </button>
                  <div class="flex flex-col gap-8 md:flex-row">

                      <Lightgallery
                          :settings="{ speed: 500, plugins: plugins }"
                          class="flex justify-center"
                      >
                          <a :href="dish.img" class="block h-48 w-64 self-center">
                              <img
                                  class="object-contain"
                                  :src="dish.thumbnail"
                                  alt=""
                              />
                          </a>
                      </Lightgallery>



                <div class="flex grow flex-col items-stretch justify-between">
                  <div class="text-left">
                    <HeadlessDialogTitle
                      as="h1"
                      class="mb-2 flex items-center gap-2 text-2xl font-medium"
                    >
                      {{ dish.name }}
                      <span class="shrink-0">
                        <Tooltip>
                          <div class="mb-4 text-center">Пищевая ценность на {{ dish.pich_cen100 ? '100' : dish.weight }} г</div>
                          <div class="flex justify-between gap-8">
                            <span class="text-nowrap"> Энерг. ценность </span>
                            <span class="text-nowrap"> {{ dishDetails?.energ_cen }} ккал </span>
                          </div>
                          <div class="flex justify-between gap-8">
                            <span>Белки</span>
                            <span> {{ dishDetails?.belki }} г </span>
                          </div>
                          <div class="flex justify-between gap-8">
                            <span>Жиры</span>
                            <span> {{ dishDetails?.ziri }} г </span>
                          </div>
                          <div class="flex justify-between gap-8">
                            <span>Углеводы</span>
                            <span> {{ dishDetails?.uglevodi }} г </span>
                          </div>
                        </Tooltip>
                      </span>
                    </HeadlessDialogTitle>
                    <div class="mb-2 text-sm">
                      <Skeleton
                        height="1.25rem"
                        width="50%"
                        :show-content="isSuccess"
                      >
                        <span v-html="dishDetails?.description"></span>
                      </Skeleton>
                    </div>
                    <div class="mb-8 text-sm lg:mb-24">
                      <Skeleton
                        height="1.25rem"
                        width="33%"
                        :show-content="isSuccess"
                      >
                        <span> {{ dishDetails?.count }} / {{ dishDetails?.weight }} гр </span>
                      </Skeleton>
                    </div>
                  </div>
                  <div class="flex gap-4">
                    <DishAdder
                      class="h-9 w-24 text-black lg:h-10 lg:w-32"
                      hide-button
                      :dish-id="dish.id"
                      :dish-name="dish.name"
                      :can-deliver="dish.can_deliver"
                      :available="dish.available"
                    />
                    <DishAdder
                      class="h-9 grow text-black lg:h-10"
                      always-button
                      :dish-id="dish.id"
                      :dish-name="dish.name"
                      :available="dish.available"
                      :can-deliver="dish.can_deliver"
                      v-if="dish.available > 0"
                    >
                      В корзину за {{ dish.price }} &#8381;
                    </DishAdder>
                  </div>
                </div>
                  </div>

                  <div v-if="dishDetails?.recommendations.length > 0" class="mt-10">
                      <h2 class="mb-5 flex items-center gap-2 text-xl font-normal">С этим часто берут</h2>
                      <div class="flex grid-cols-3 gap-4 scrollbar-track-black/5 scrollbar-thumb-black/10 hover:scrollbar-thumb-black/15 md:grid">

                          <template
                              v-for="recommendedDish in dishDetails?.recommendations"
                              :key="recommendedDish.id"
                          >
                              <div class="min-w-[40%] flex">
                                  <DishCard
                                      :dish="recommendedDish"
                                      :big_card="false"
                                      :size="'small'"
                                      :show-weight="false"
                                  />
                              </div>

                          </template>
                      </div>
                  </div>

              </div>

            </HeadlessDialogPanel>
          </HeadlessTransitionChild>
        </div>
      </div>
    </HeadlessDialog>
  </HeadlessTransitionRoot>
</template>

<script setup lang="ts">
import { ref, toRefs } from 'vue'
import type { Dish } from '~/interfaces/dishes'
import Lightgallery from 'lightgallery/vue';
import lgThumbnail from 'lightgallery/plugins/thumbnail';
import lgZoom from 'lightgallery/plugins/zoom';

const plugins = [lgThumbnail, lgZoom]

const props = defineProps<{
  show?: boolean
  dish: Dish
}>()
const { show, dish } = toRefs(props)
const emit = defineEmits(['close'])

const { data: dishDetails, isSuccess } = useDish(dish.value.link?.split('/').at(-1) || '', show)

const dialogPanelEl = ref<HTMLElement>()
</script>
<style lang="css">
@import 'lightgallery/css/lightgallery.css';
@import 'lightgallery/css/lg-thumbnail.css';
@import 'lightgallery/css/lg-zoom.css';
</style>
